import { create } from "zustand";
import _, { isEmpty, isPlainObject } from 'lodash';
import { persist } from "zustand/middleware";
import {
  ENQUIRY_OPTIONS_LABELS,
  ENQUIRY_OPTIONS_VALUES,
  ERROR_MESSAGES,
  GA_EVENTS,
  HTTP_STATUS_OK,
  SUCCESS_MAIL,
  reducerKeys,
} from "../constants/appConstants";
import {
  getAboutPage,
  getBAC_Brands,
  getBrandData,
  getCareersPage,
  getContactSection,
  getFooter,
  getHomePage,
  getLocations,
  getOurBrands,
  getOurPeoples,
  getPoliciesPage,
  getPolicySubpage,
  getRouteList,
  getStateList,
  getStates,
  getTeamList,
  getTermsConditions,
  getTimeLine,
} from "../services/cmsService";
import { contact } from "../services/contactService";
import { logGoogleTagsEvents } from "../utils/gTags/gTags";
import { BackEndValidationError, BackEndValidationErrorResponse } from "../types";

interface FormData {
  fullName: string;
  email: string;
  phone: string;
  enquiry: string;
  message: string;
  terms: boolean;
  brand?: string;
  state?: string;
  location?: string;
  make?: string;
  model?: string;
  year?: string;
  odometer?: string;
  vin?: string;
  recaptchaToken?: string;
}

interface FieldError {
  message: string;
  isFails: boolean;
}

interface CMSStore {
  routeList: any;
  aboutPage: any;
  contactSection: any;
  homePage: any;
  footer: any;
  careersPage: any;
  someOfOurTeam: any[];
  bacBrands: any[];
  states: any[];
  statesList: any[];
  locations: any[];
  isLoadingStatesList: boolean;
  isLoadingBrands: boolean;
  isLoadingLocations: boolean;
  isLoadingStates: boolean;
  show404: boolean;
  ourBrands: any[];
  ourPeoples: any;
  timeLine: any;
  loading: boolean;
  loadingRoute: boolean;
  contactSubmitLoading: boolean;
  isSuccessToSendEmail: boolean;
  isSubmissionFailed: boolean;
  submissionValidationError: BackEndValidationError | null;
  selectedBrand: any | null;
  setIsSuccessToSendEmail: (value: boolean) => void;
  setIsSubmissionFailed: (value: boolean) => void;
  error: string | null;
  formData: FormData;
  countryCode: any;
  phoneNumber: string;
  subpageTitle: string | null;
  dialCode: string;
  heroHeaderSectionData: any;
  setDialCode: (dialCode: string) => void;
  setCountryCode: (code: string) => void;
  setPhoneNumber: (phone: string) => void;
  updateFormData: (field: keyof FormData, value: string) => void;
  fetchAboutPage: () => Promise<void>;
  fetchHomePage: () => Promise<void>;
  fetchTimeline: () => Promise<void>;
  fetchRouteList: () => Promise<void>;
  fetchCareersPage: () => Promise<void>;
  fetchTeamList: () => Promise<void>;
  fetchOurPeoples: () => Promise<void>;
  fetchOurBrands: () => Promise<void>;
  setFieldErrors: (
    field: string,
    errorMessage: string,
    isFails: boolean
  ) => void;
  clearAllFieldErrors: () => void;
  fieldErrors: Record<string, FieldError>;
  resetForm: () => void;
  fetchFooter: () => Promise<void>;
  submitContactForm: (token?: string) => Promise<void>;
  policySubpage: any | null;
  fetchPolicySubpage: (slug: string) => Promise<void>;
  policies: any | null;
  getPoliciesPage: () => Promise<void>;
  termsConditions: any | null;
  fetchTermsConditions: () => Promise<void>;
  fetchContactSection: () => Promise<void>;
  addSubpageTitle: (title: string) => void;
  addHeroHeaderDetails: (data: any) => void;
  getBrandData: (brandId: any) => Promise<any | undefined>;
  getBAC_Brands: () => Promise<void>;
  clearBuyACarData: () => void;
  fetchStates: (brandId: any) => Promise<void>;
  fetchStatesList: () => Promise<void>;
  fetchLocations: (brandId: any, stateId: any) => Promise<void>;
}

const useCmsStore = create<CMSStore>()(
  persist(
    (set) => ({
      heroHeaderSectionData: {},
      routeList: [],
      timeLine: [],
      ourPeoples: [],
      selectedBrand: null,
      isLoadingLocations: false,
      isLoadingStates: false,
      isLoadingStatesList: false,
      isLoadingBrands: false,
      ourBrands: [],
      bacBrands: [],
      states: [],
      statesList: [],
      locations: [],
      aboutPage: null,
      contactSection: null,
      subpageTitle: null,
      footer: null,
      homePage: null,
      careersPage: null,
      someOfOurTeam: [],
      loading: false,
      loadingRoute: false,
      show404: false,
      contactSubmitLoading: false,
      isSuccessToSendEmail: false,
      setIsSuccessToSendEmail: (value: boolean) => {
        set({ isSuccessToSendEmail: value });
      },
      isSubmissionFailed: false,
      submissionValidationError: null,
      setIsSubmissionFailed: (value: boolean) => {
        set({ isSubmissionFailed: value });
      },
      error: null,
      formData: {
        fullName: "",
        email: "",
        phone: "",
        enquiry: "",
        message: "",
        brand: "",
        state: "",
        location: "",
        make: "",
        model: "",
        year: "",
        odometer: "",
        vin: "",
        terms: false,
      },
      countryCode: "au",
      phoneNumber: "",
      dialCode: "+61",
      setDialCode: (dialCode: string) => set({ dialCode }),
      setCountryCode: (code: string) => set({ countryCode: code }),
      setPhoneNumber: (phone: string) => set({ phoneNumber: phone }),
      updateFormData: (field: any, value: any) => {
        set((state) => ({
          formData: {
            ...state.formData,
            [field]: value,
          },
        }));
      },
      addHeroHeaderDetails: (data: any) => {
        set({ heroHeaderSectionData: data });
      },

      submitContactForm: async (token?: string) => {
        set({
          contactSubmitLoading: true,
          error: null,
          submissionValidationError: null,
          isSubmissionFailed: false,
        });
        try {
          const { formData } = useCmsStore.getState();

          let payloadEnquiryCode = "";
          switch (formData?.enquiry) {
            case ENQUIRY_OPTIONS_LABELS?.GENERAL_ENQUIRY:
              payloadEnquiryCode = ENQUIRY_OPTIONS_VALUES?.GENERAL_ENQUIRY;
              break;

              case ENQUIRY_OPTIONS_LABELS.BUY_A_CAR:
                payloadEnquiryCode = ENQUIRY_OPTIONS_VALUES.BUY_A_CAR;
              break;

              case ENQUIRY_OPTIONS_LABELS.SELL_YOUR_CAR:
                payloadEnquiryCode = ENQUIRY_OPTIONS_VALUES.SELL_YOUR_CAR;
              break;

              case ENQUIRY_OPTIONS_LABELS.CAREERS_AND_OPPORTUNITIES:
                payloadEnquiryCode = ENQUIRY_OPTIONS_VALUES.CAREERS_AND_OPPORTUNITIES;
              break;

              case ENQUIRY_OPTIONS_LABELS.PARTNERSHIPS_AND_BUSINESS:
                payloadEnquiryCode = ENQUIRY_OPTIONS_VALUES.PARTNERSHIPS_AND_BUSINESS;
              break;

            default:
              console.warn("Unknown enquiry type:", formData?.enquiry);
              break;
          }
          const apiPayload = _.cloneDeep(formData);
          apiPayload.enquiry = payloadEnquiryCode;
          apiPayload.recaptchaToken = token || "recaptcha token placeholder";  
          const response = await contact(apiPayload);

            const responseStatus = response && response.status;
            const responseMessage = response && response.data && response.data.message;

            if (
              responseStatus === HTTP_STATUS_OK &&
              responseMessage === SUCCESS_MAIL
            ) {
            useCmsStore.getState().resetForm();
          }

          set({
            contactSubmitLoading: false,
            isSuccessToSendEmail: true,
            isSubmissionFailed: false,
            submissionValidationError: null,
            phoneNumber: "",
            dialCode: "+61",
            countryCode: "au",
            formData: {
              fullName: "",
              email: "",
              phone: "",
              enquiry: "",
              message: "",
              terms: false,
              brand: "",
              state: "",
              location: "",
              make: "",
              model: "",
              year: "",
              odometer: "",
              vin: "",
            },
          });
          logGoogleTagsEvents(GA_EVENTS.CONTACT_FORM_SUBMIT_SUCCESS);
        } catch (error) {
          logGoogleTagsEvents(GA_EVENTS.CONTACT_FORM_SUBMIT_FAIL);
          set({ isSubmissionFailed: true });
          const validationError = (error as BackEndValidationErrorResponse)?.response?.data?.message ?? null;
          if (isPlainObject(validationError)) {
            set({ submissionValidationError: validationError });
          }
          const message =
            error instanceof Error
              ? error.message
              : ERROR_MESSAGES.CONTACT_US_FAILED;
          set({ error: message, contactSubmitLoading: false });
        }
      },

      fetchAboutPage: async () => {
        set({ loading: true, error: null });
        try {
          const data = await getAboutPage();
          set({ aboutPage: data, loading: false });
        } catch (error) {
          const message =
            error instanceof Error
              ? error.message
              : ERROR_MESSAGES.FETCH_ABOUT_PAGE;
          set({
            error: message,
            loading: false,
          });
        }
      },
      fetchHomePage: async () => {
        set({ loading: true, error: null });
        try {
          const data = await getHomePage();
          set({ homePage: data, loading: false });
        } catch (error) {
          const message =
            error instanceof Error
              ? error.message
              : ERROR_MESSAGES.FETCH_HOME_PAGE;
          set({
            error: message,
            loading: false,
          });
        }
      },
      fetchCareersPage: async () => {
        set({ loading: true, error: null });
        try {
          const data = await getCareersPage();
          set({ careersPage: data, loading: false });
        } catch (error) {
          const message =
            error instanceof Error
              ? error.message
              : ERROR_MESSAGES.FETCH_CAREER_PAGE;
          set({
            error: message,
            loading: false,
          });
        }
      },
      fetchFooter: async () => {
        set({ loading: true, error: null });
        try {
          const data = await getFooter();
          set({ footer: data, loading: false });
        } catch (error) {
          const message =
            error instanceof Error
              ? error.message
              : ERROR_MESSAGES.FETCH_FOOTER;
          set({
            error: message,
            loading: false,
          });
        }
      },
      fetchTeamList: async () => {
        set({ loading: true, error: null });
        try {
          const data = await getTeamList();
          set({ someOfOurTeam: data, loading: false });
        } catch (error) {
          const message =
            error instanceof Error ? error.message : ERROR_MESSAGES.FETCH_TEAM;
          set({
            error: message,
            loading: false,
          });
        }
      },
      fetchTimeline: async () => {
        set({ loading: true, error: null });
        try {
          const data = await getTimeLine();
          set({ timeLine: data, loading: false });
        } catch (error) {
          const message =
            error instanceof Error
              ? error.message
              : ERROR_MESSAGES.FETCH_ROUTE_LIST;
          set({
            error: message,
            loading: false,
          });
        }
      },
      fetchRouteList: async () => {
        set({ loadingRoute: true, error: null });
        try {
          const data = await getRouteList();
          set({ routeList: data, loadingRoute: false });
        } catch (error) {
          const message =
            error instanceof Error
              ? error.message
              : ERROR_MESSAGES.FETCH_TIMELINE;
          set({
            error: message,
            loadingRoute: false,
          });
        }
      },
      fetchOurPeoples: async () => {
        set({ loading: true, error: null });
        try {
          const data = await getOurPeoples();
          set({ ourPeoples: data, loading: false });
        } catch (error) {
          const message =
            error instanceof Error
              ? error.message
              : ERROR_MESSAGES.FETCH_OUR_PEOPLES;
          set({
            error: message,
            loading: false,
          });
        }
      },
      fetchOurBrands: async () => {
        set({ loading: true, error: null });
        try {
          const data = await getOurBrands();
          set({ ourBrands: data, loading: false });
        } catch (error) {
          const message =
            error instanceof Error
              ? error.message
              : ERROR_MESSAGES.FETCH_BRANDS;
          set({
            error: message,
            loading: false,
          });
        }
      },
      getBAC_Brands: async () => {
        set({
          loading: true,
          error: null,
          bacBrands: [],
          states: [],
          locations: [],
          statesList: [],
          isLoadingBrands: true,
        });
        try {
          const data = await getBAC_Brands();
          set({
            bacBrands: data,
            loading: false,
            isLoadingBrands: false,
          });
        } catch (error) {
          const message =
            error instanceof Error
              ? error.message
              : ERROR_MESSAGES.FETCH_BRANDS;
          set({
            error: message,
            loading: false,
            isLoadingBrands: false,
          });
        }
      },
      fetchStates: async (brandId) => {
        set({
          loading: true,
          error: null,
          states: [],
          locations: [],
          statesList: [],
          isLoadingStates: true,
        });
        try {
          const data = await getStates(brandId);
          set({
            states: data,
            loading: false,
            isLoadingStates: false,
          });
        } catch (error) {
          const message =
            error instanceof Error
              ? error.message
              : ERROR_MESSAGES.FETCH_BRANDS;
          set({
            error: message,
            loading: false,
            isLoadingStates: false,
          });
        }
      },
      fetchStatesList: async () => {
        set({
          loading: true,
          error: null,
          states: [],
          locations: [],
          bacBrands: [],
          statesList: [],
          isLoadingStatesList: true,
        });
        try {
          const data = await getStateList();
          set({ statesList: data, loading: false, isLoadingStatesList: false });
        } catch (error) {
          const message =
            error instanceof Error
              ? error.message
              : ERROR_MESSAGES.FETCH_BRANDS;
          set({
            error: message,
            loading: false,
            isLoadingStatesList: false,
          });
        }
      },
      fetchLocations: async (brandId, stateId) => {
        set({
          loading: true,
          error: null,
          statesList: [],
          isLoadingLocations: true,
        });
        try {
          const data = await getLocations(brandId, stateId);
          set({ locations: data, loading: false, isLoadingLocations: false });
        } catch (error) {
          const message =
            error instanceof Error
              ? error.message
              : ERROR_MESSAGES.FETCH_BRANDS;
          set({
            error: message,
            loading: false,
            isLoadingLocations: false,
          });
        }
      },
      getBrandData: async (brandId: any) => {
        set({ loading: true, error: null, selectedBrand: null });
        try {
          const data = await getBrandData(brandId);
          set({ selectedBrand: data, loading: false });
          return data;
        } catch (error) {
          const message =
            error instanceof Error
              ? error.message
              : ERROR_MESSAGES.FETCH_BRANDS;
          set({
            error: message,
            loading: false,
            selectedBrand: null,
          });
          throw error;
        }
      },
      fieldErrors: {},
      setFieldErrors: (
        field: string,
        errorMessage: string,
        isFails: boolean
      ) => {
        set((state) => ({
          fieldErrors: {
            ...state.fieldErrors,
            [field]: { message: errorMessage, isFails },
          },
        }));
      },
      clearAllFieldErrors: () => {
        set({
          fieldErrors: {},
          error: null,
          submissionValidationError: null,
          isSubmissionFailed: false,
        });
      },
      clearBuyACarData: () => {
        set({ states: [], locations: [], bacBrands: [] });
      },
      resetForm: () => {
        set({
          formData: {
            fullName: "",
            email: "",
            phone: "",
            enquiry: "",
            message: "",
            terms: false,
            brand: "",
            state: "",
            location: "",
            make: "",
            model: "",
            year: "",
            odometer: "",
            vin: "",
          },
          fieldErrors: {},
          isSubmissionFailed: false,
          submissionValidationError: null,
          phoneNumber: "",
          dialCode: "+61",
          countryCode: "au",
          bacBrands: [],
          states: [],
          locations: [],
        });
      },
      policySubpage: null,
      // Function to fetch policy subpage by slug return 404 page if not found
      fetchPolicySubpage: async (slug: string) => {
        set({
          loading: true,
          error: null,
          policySubpage: null,
          subpageTitle: null,
          show404: false,
        });
        try {
          const data = await getPolicySubpage(slug);
          const isNotFound = isEmpty(data);
          set({
            policySubpage: isEmpty(data) ? null : data,
            loading: false,
            subpageTitle: data.title,
            show404: isNotFound,
          });
        } catch (error) {
          const statusCode =
            (error as { response?: { status?: number } })?.response?.status;

          set({
            policySubpage: null,
            loading: false,
            subpageTitle: null,
            show404: statusCode === 404,
          });
        }
      },
      policies: null,
      addSubpageTitle: (title: string) => {
        set({ subpageTitle: title });
      },
      getPoliciesPage: async () => {
        set({ loading: true, error: null, policies: null });
        try {
          const data = await getPoliciesPage();
          set({ policies: data, loading: false });
        } catch (error) {
          const message =
            error instanceof Error
              ? error.message
              : ERROR_MESSAGES.FETCH_POLICY;
          set({ error: message, loading: false });
        }
      },
      termsConditions: null,
      fetchTermsConditions: async () => {
        set({ loading: true, error: null, termsConditions: null });
        try {
          const data = await getTermsConditions();
          set({ termsConditions: data, loading: false });
        } catch (error) {
          const message =
            error instanceof Error
              ? error.message
              : ERROR_MESSAGES.FETCH_TERMS_PAGE;
          set({ error: message, loading: false });
        }
      },
      fetchContactSection: async () => {
        set({ loading: true, error: null });
        try {
          const data = await getContactSection();
          set({ contactSection: data, loading: false });
        } catch (error) {
          const message =
            error instanceof Error
              ? error.message
              : ERROR_MESSAGES.FETCH_CONTACT_SECTION;
          set({
            error: message,
            loading: false,
          });
        }
      },
    }),
    {
      name: reducerKeys.CMS_STORE,
      partialize: (state) => {
        const { isSuccessToSendEmail, submissionValidationError, ...rest } = state;
        return rest;
      },
    }
  )
);

export default useCmsStore;
